import { useState } from 'react';
import type { Item, Fib, Scores } from '@mas-flow/types';
import { useCan } from '../auth/AuthProvider';
import { useCastVote } from '../hooks/useVote';
import { useToast } from '../hooks/useToast';
import { ApiHttpError } from '../api/client';

const FIB: Fib[] = [1, 3, 5, 8, 13];

export interface InlineScoreCellProps {
  item: Item;
  field: keyof Scores;
}

export function InlineScoreCell({ item, field }: InlineScoreCellProps) {
  const canEdit = useCan('item.score', item);
  const [editing, setEditing] = useState(false);
  const cast = useCastVote();
  const toast = useToast();
  const value = item.scores[field];

  if (!canEdit) {
    return <span className="text-slate-700">{value ?? '—'}</span>;
  }

  if (!editing) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        className="rounded px-1 text-slate-700 hover:bg-slate-100"
      >
        {value ?? '—'}
      </button>
    );
  }

  const submit = (v: Fib) => {
    setEditing(false);
    cast.mutate(
      { itemId: item.id, partial: { scores: { [field]: v } } },
      {
        onError: (err) => {
          if (err instanceof ApiHttpError && err.status === 403) {
            toast('You no longer have permission to score this item');
          } else {
            toast('Vote failed');
          }
        },
      },
    );
  };

  return (
    <div
      className="flex items-center gap-0.5 rounded border border-brand-tint bg-brand-wash p-0.5"
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => { if (e.key === 'Escape') setEditing(false); }}
    >
      {FIB.map((v) => (
        <button
          key={v}
          type="button"
          aria-label={`Vote ${field} as ${v}`}
          onClick={() => submit(v)}
          className={`rounded px-1.5 py-0.5 text-xs ${value === v ? 'bg-brand text-white' : 'bg-white text-slate-700 hover:bg-brand-wash'}`}
        >
          {v}
        </button>
      ))}
    </div>
  );
}
